import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { reverseGeocode } from "./geo";
import { getGeoPosition, checkGeoPermission } from "./geolocation";
import { isNative } from "./native";

const LocationCtx = createContext(null);

const DEFAULT = { city: "Sorriso", uf: "MT" };
// V18.2 — Considera a geolocalização "fresca" por 15 minutos. Após esse prazo,
// tentamos atualizar automaticamente no próximo mount / visibility change.
const GEO_STALENESS_MS = 15 * 60 * 1000;

export function LocationProvider({ children }) {
  const [loc, setLoc] = useState(() => {
    try {
      const raw = localStorage.getItem("aj_location_v2");
      if (raw) return JSON.parse(raw);
    } catch (e) { console.error("[location] read storage failed:", e); }
    return DEFAULT;
  });

  // V12.5/V18.2: ao abrir o app OU voltar do background, pega a localização silenciosamente
  // e re-resolve a cidade se a última resolução for antiga (> 15min) — antes ficava travada
  // no valor da primeira resolução (bug: item aparecia a 500m quando o usuário estava a 4km).
  // Escolha MANUAL do usuário sempre pesa mais (não sobrescreve).
  const refreshGeo = useCallback(async (force = false) => {
    try {
      const cur = JSON.parse(localStorage.getItem("aj_location_v2") || "null") || {};
      if (cur?.manual) return; // usuário escolheu manualmente → respeita
      const age = cur?.resolved_at ? Date.now() - cur.resolved_at : Infinity;
      if (!force && cur?.geo_resolved && age < GEO_STALENESS_MS) return; // ainda fresca
    } catch { /* ignore parse errors */ }
    // V18.9.5 — Evita disparar o prompt do sistema se o usuário nunca aprovou.
    // V18.9.12 — Exceção: no APP NATIVO a permissão do SO é persistente, então
    // pedimos UMA única vez (flag local) para detectar a cidade automaticamente —
    // sem isso quem abre o app em Sinop ficava vendo "Sorriso" para sempre.
    // No navegador nunca promptamos sozinhos (lá o prompt reaparece a cada sessão).
    try {
      const perm = await checkGeoPermission();
      if (perm !== "granted" && !force) {
        if (perm === "denied") return;
        let askedOnce = false;
        try { askedOnce = localStorage.getItem("aj_geo_prompted") === "1"; } catch { /* noop */ }
        if (!isNative() || askedOnce) return;
        try { localStorage.setItem("aj_geo_prompted", "1"); } catch { /* noop */ }
      }
    } catch { /* segue o baile */ }
    try {
      const pos = await getGeoPosition({ timeout: 15000, maximumAge: 60000 });
      const { latitude, longitude } = pos.coords;
      setLoc((c) => ({ ...c, lat: latitude, lng: longitude }));
      const d = await reverseGeocode(latitude, longitude);
      setLoc((c) => ({
        ...c,
        lat: latitude,
        lng: longitude,
        city: (d && d.city) ? d.city : c.city,
        uf: (d && d.uf) ? d.uf : c.uf,
        geo_resolved: true,
        resolved_at: Date.now(),
      }));
    } catch (e) {
      // negou / timeout / indisponível → mantém última (ou default)
    }
  }, []);

  // Mount: refresca se estiver stale (não força — respeita cache recente)
  useEffect(() => {
    refreshGeo(false);
  }, [refreshGeo]);

  // Voltar do background/foco: re-avalia. Se stale (>15min), refresca.
  useEffect(() => {
    const onFocus = () => refreshGeo(false);
    const onVis = () => { if (document.visibilityState === "visible") refreshGeo(false); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refreshGeo]);

  useEffect(() => {
    try {
      localStorage.setItem("aj_location_v2", JSON.stringify(loc));
    } catch (e) { console.error("[location] persist failed:", e); }
  }, [loc]);

  const setLocation = (next) => setLoc((prev) => ({ ...prev, ...next }));

  const value = useMemo(() => ({
    location: loc,
    setLocation,
    // Expõe pro usuário forçar via botão "Usar minha localização"
    refreshGeo: () => refreshGeo(true),
  }), [loc, refreshGeo]);

  return <LocationCtx.Provider value={value}>{children}</LocationCtx.Provider>;
}

export const useLocation = () => useContext(LocationCtx);
